/**
 * Окно «Выдать» холд (16.10.2026, владелец: «нажатие кнопки "выдать" продумай. Все случаи — ошибки, двойные нажатия
 * и так далее»). Сумма — NumPadPop (A27), «Весь холд» одним касанием, дата ‹ › (сегодня … 14 дней назад — выдали
 * вчера, внесли сегодня), комментарий — TextPad. Случаи:
 *  • двойное нажатие — sending-ref (синхронно) + busy; окно во время записи не закрывается;
 *  • обрыв связи / потерянный ответ — тот же request_id на повтор: сервер вернёт уже записанную, второй не будет;
 *  • холд изменился (второй ОО выдал, неделю пересчитали) — сервер 409: подставляем новый холд, просим проверить;
 *  • сумма сильно больше холда (опечатка 104 000) — сервер 409 overpay: отдельное «Да, выдать» или «Изменить сумму»;
 *  • повтор вернул выдачу с другой суммой — честно говорим «уже записана ₽X», окно не закрываем молча.
 * Ответ применяется только к тому же открытию и живому компоненту (genRef/aliveRef, как BonusPenaltySheet).
 */
import { useEffect, useRef, useState } from "react";
import { NumPadPop, SheetDialog, TextPad } from "../../components/shared";
import { addDays, fmtDate, fmtDateApi, todayBusiness } from "../../utils/date";
import { holdNote, holdTone, newRequestId, orenStamp, postPayout, rub, signedRub, type HoldRow, type Payout, type Recent } from "../../hooks/payoutsApi";
import css from "./hold.module.css";

const HOLD_START = Date.UTC(2026, 9, 9);   // 09.10.2026 — раньше выдачи не записываем (сервер так же)
const BACK_DAYS = 14;

export interface PayDone { payout: Payout; holdAfter: number; name: string }
interface Props {
  open: boolean; row: HoldRow | null; onClose: () => void; onDone: (d: PayDone) => void; onStale: () => void;
  /** Ключ выдачи с неизвестным исходом (обрыв, 5xx): переживает закрытие окна — повтор не запишет вторую (ревью 16.10). */
  pendingId?: string; onPending: (staffId: string, id: string | null) => void;
}

export function PayoutSheet({ open, row, onClose, onDone, onStale, pendingId, onPending }: Props) {
  const [amount, setAmount] = useState(0);
  const [comment, setComment] = useState("");
  const [day, setDay] = useState<Date>(() => todayBusiness());
  const [hold, setHold] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [overpay, setOverpay] = useState<{ after: number; error: string } | null>(null);
  const [recent, setRecent] = useState<Recent[]>([]);
  const [dayTouched, setDayTouched] = useState(false);
  const [np, setNp] = useState<HTMLElement | null>(null);
  const [pad, setPad] = useState(false);
  const reqId = useRef("");
  const sending = useRef(false);
  const genRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  useEffect(() => {
    if (!open || !row) return;
    genRef.current += 1;
    reqId.current = pendingId ?? newRequestId();
    sending.current = false;
    setAmount(0); setComment(""); setDay(todayBusiness()); setHold(row.hold); setRecent([]); setDayTouched(false);
    setBusy(false); setErr(null);
    setNotice(pendingId ? "Прошлая выдача этому человеку не подтвердилась. Если вводите ту же сумму, повтор безопасен: дважды не запишется" : null);
    setOverpay(null); setNp(null); setPad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);
  const dismiss = () => { if (!busy) onClose(); };

  const today = todayBusiness();
  const minDay = new Date(Math.max(addDays(today, -BACK_DAYS).getTime(), HOLD_START));
  const after = hold - amount;
  const send = async (confirm: boolean) => {
    if (!row || amount <= 0 || sending.current) return;
    sending.current = true;
    setBusy(true); setErr(null); setNotice(null);
    if (!confirm) setOverpay(null);
    const gen = genRef.current;
    const sid = row.staff_id, key = reqId.current;
    const r = await postPayout({ staff_id: sid, amount, expected_hold: hold, request_id: key, ...(dayTouched ? { day: fmtDateApi(day) } : {}), comment, ...(confirm ? { confirm_overpay: true } : {}) });
    sending.current = false;
    /* исход неизвестен — ключ держим за человеком даже после закрытия окна; известен — отпускаем */
    onPending(sid, !r.ok && (r.code === "unknown" || r.code === "overpay") ? key : null);
    if (!aliveRef.current || genRef.current !== gen) return;
    setBusy(false);
    if (r.ok) { onDone({ payout: r.payout, holdAfter: r.holdAfter, name: row.name }); return; }
    if (r.code === "hold_changed") { setHold(r.hold); setRecent(r.recent); setOverpay(null); setNotice(r.error); onStale(); return; }
    if (r.code === "overpay") { setOverpay({ after: r.after, error: r.error }); return; }
    if (r.code === "cancelled" || r.code === "request_reused") { reqId.current = newRequestId(); setNotice(r.error); onStale(); return; }
    if (r.code === "unknown") onStale();
    setErr(r.error);
  };

  const tone = (v: number) => css[holdTone(v)] ?? "";
  return (
    <>
      <SheetDialog open={open} onDismiss={dismiss} ariaLabel="Выдать холд" maxHeight="86dvh">
        <div className={css.sheetHead}>
          <span className={css.sheetTitle}>Выдать · {row?.name ?? ""}</span>
        </div>
        <div className={css.sheetBody}>
          <div className={css.nowLine}>Холд сейчас: <b className={tone(hold)}>{signedRub(hold)}</b> · {holdNote(hold)}</div>
          {hold <= 0 ? <div className={css.hint}>Мы ничего не должны — эта выдача будет авансом, оператор станет должен.</div> : null}
          <div className={css.field}>
            <span className={css.fLbl}>Сумма, ₽</span>
            <div className={css.amountRow}>
              <button type="button" className={`${css.amount} ${amount > 0 ? css.amountOn : ""}`} disabled={busy} onClick={(e) => setNp(e.currentTarget)}>
                {amount > 0 ? rub(amount) : "сколько выдали…"}
              </button>
              {hold > 0 ? <button type="button" className={css.allBtn} disabled={busy} onClick={() => { setAmount(hold); setOverpay(null); }}>Весь холд {rub(hold)}</button> : null}
            </div>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Когда выдали</span>
            <div className={css.dateRow}>
              <button type="button" className={css.dateBtn} aria-label="Днём раньше" disabled={busy || day.getTime() <= minDay.getTime()} onClick={() => { setDayTouched(true); setDay((d) => addDays(d, -1)); }}>‹</button>
              <span className={css.dateVal}>{day.getTime() === today.getTime() ? "сегодня" : fmtDate(day)}</span>
              <button type="button" className={css.dateBtn} aria-label="Днём позже" disabled={busy || day.getTime() >= today.getTime()} onClick={() => { setDayTouched(true); setDay((d) => addDays(d, 1)); }}>›</button>
            </div>
          </div>
          <div className={css.field}>
            <span className={css.fLbl}>Комментарий · увидит оператор · необязательно</span>
            <button type="button" className={`${css.chip} ${comment ? "" : css.chipEmpty}`} disabled={busy} onClick={() => setPad(true)}>{comment || "например «наличными» или «на карту»"}</button>
          </div>
          {amount > 0 ? <div className={css.nowLine}>После выдачи холд: <b className={tone(after)}>{signedRub(after)}</b> · {after > 0 ? "останемся должны" : after < 0 ? "будет должен нам" : "в расчёте"}</div> : null}
          {notice ? <div className={css.notice}>{notice}</div> : null}
          {recent.length ? (
            <div className={css.notice}>Только что этому человеку записано: {recent.map((x) => `${rub(x.amount)} в ${orenStamp(x.created_at).slice(6)}${x.created_by_name ? ` (${x.created_by_name})` : ""}`).join(", ")}. Если это та же выдача — ничего не нажимайте.</div>
          ) : null}
          {overpay ? (
            <div className={css.over}>
              <span>{overpay.error}</span>
              <div className={css.overBtns}>
                <button type="button" className={css.overYes} disabled={busy} onClick={() => void send(true)}>Да, выдать {rub(amount)}</button>
                <button type="button" className={css.overNo} disabled={busy} onClick={() => { setOverpay(null); setNp(null); }}>Изменить сумму</button>
              </div>
            </div>
          ) : null}
          {err ? <div className={css.err}>{err}</div> : null}
        </div>
        <div className={css.foot}>
          <button type="button" className={css.btnCancel} disabled={busy} onClick={dismiss}>Отмена</button>
          <button type="button" className={css.btnGo} disabled={busy || amount <= 0 || !!overpay} onClick={() => void send(false)}>
            {busy ? "Записываю…" : amount > 0 ? `${recent.length ? "Выдать ещё" : "Выдать"} ${rub(amount)}` : "Выдать"}
          </button>
        </div>
      </SheetDialog>
      <NumPadPop open={!!np} anchor={np} label="Сколько выдали" mode="money" value={amount || ""} max={300000}
        onCommit={(v) => { setAmount(Math.max(0, Math.round(v))); setOverpay(null); }} onClose={() => setNp(null)} />
      <TextPad open={pad} label="Комментарий к выдаче" value={comment} maxLength={200} placeholder="наличными / на карту…"
        onCommit={(v) => setComment(v.trim())} onClose={() => setPad(false)} />
    </>
  );
}
